import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { Trans, useLingui } from '@lingui/react/macro';
import { useTheme } from '../contexts/ThemeContext';

type Status = 'verifying' | 'success' | 'expired' | 'error';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLingui();
  const { isDark } = useTheme();
  const [status, setStatus] = useState<Status>('verifying');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage(t`Missing verification token. Please use the link from your email.`);
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await authService.verifyEmail(token);
      if (cancelled) return;
      if (result.success) {
        setStatus('success');
        const { onboardingService } = await import('../services/onboardingService');
        try {
          const done = await onboardingService.getStatus();
          setTimeout(() => navigate(done ? '/dashboard' : '/onboarding'), 1200);
        } catch {
          setTimeout(() => navigate('/onboarding'), 1200);
        }
      } else if (result.expired) {
        setStatus('expired');
        setEmail(result.email || '');
        setMessage(result.error || t`This verification link has expired.`);
      } else {
        setStatus('error');
        setMessage(result.error || t`Verification failed.`);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate]);

  const handleResend = async () => {
    if (!email) return;
    setResendBusy(true);
    setResendMessage('');
    const r = await authService.resendVerification(email);
    setResendBusy(false);
    setResendMessage(r.message || (r.success ? t`If that account exists, a new verification email has been sent.` : t`Failed to resend.`));
  };

  return (
    <div
      className="p-4 sm:p-8"
      style={{
        fontFamily: "'Inter', sans-serif",
        backgroundColor: 'var(--color-bg)',
        color: 'var(--color-text)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div className="max-w-md w-full">
        <div className="glass-panel rounded-3xl p-6 sm:p-8 border border-blue-100 dark:border-blue-900/30">
          <div className="text-center mb-6">
            <img
              src={isDark ? '/privacypal-logo-light.png' : '/privacypal-logo.png'}
              alt="PrivacyPal"
              className="w-48 sm:w-64 h-auto prism-edge mx-auto"
            />
          </div>

          {status === 'verifying' && (
            <div className="text-center">
              <div className="spinner mx-auto mb-3"></div>
              <p className="text-sm text-slate-600 dark:text-slate-400"><Trans>Verifying your email…</Trans></p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-2"><Trans>Email verified</Trans></h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                <Trans>You're signed in. Redirecting…</Trans>
              </p>
            </div>
          )}

          {status === 'expired' && (
            <div>
              <h2 className="text-xl font-semibold mb-2 text-center"><Trans>Link expired</Trans></h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 text-center mb-4">{message}</p>
              {email && (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendBusy}
                  className="w-full py-2 bg-slate-900 dark:bg-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {resendBusy ? t`Sending…` : t`Send a new link to ${email}`}
                </button>
              )}
              {resendMessage && (
                <p className="mt-3 text-sm text-center text-slate-600 dark:text-slate-400">{resendMessage}</p>
              )}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Trans>Back to sign in</Trans>
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div>
              <h2 className="text-xl font-semibold mb-2 text-center"><Trans>Verification failed</Trans></h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 text-center mb-4">{message}</p>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Trans>Back to sign in</Trans>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
